import { Anchor, Button, Heading, List, Modal, Paragraph } from '@zillow/constellation-10';
import { EXTERNAL_URLS, FAIR_HOUSING_URL } from '../../constants/urls';
import type { UserStatus } from '../../graphql/queries';
import { getDaysLeftOnPayment } from '../../utils/user-statuses';
import { pluralize } from '../../utils/formatters';
import type { SubmittedApplication, SubmittedOrSharedApplication } from '../../contexts/BaseContext';
import { SUBMISSION_TYPES } from '../../contexts/BaseContext';
import { usePurchaseExpectationsTreatment } from '../../utils/ab-tests';
import {
  emitPostSubmitApplyToMorePropertiesCtaEvent,
  emitPostSubmitParticipatingPropertiesLinkEvent,
} from '../../analytics/clickstreamEvents';
import { useRenterRequestedZillowPayments } from '../../utils/rentPaymentQueries';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

type Props = {
  onClose: () => void;
  userStatuses: Array<UserStatus>;
  submittedApplication: SubmittedOrSharedApplication;
};

function isSubmittedApplication(app: SubmittedOrSharedApplication): app is SubmittedApplication {
  return app.type === SUBMISSION_TYPES.SUBMITTED;
}

function getSearchResultsPageUrl(city?: string | null, state?: string | null): string {
  if (city && state) {
    // Format: /city-state/rentals/ (e.g., /austin-tx/rentals/)
    const formattedCity = city.toLowerCase().replace(/\s+/g, '-');
    const formattedState = state.toLowerCase();

    // Query params needed for enabling "accepts zillow applications filter"
    const queryParams = {
      filterState: {
        fr: { value: true },
        fsba: { value: false },
        fsbo: { value: false },
        nc: { value: false },
        cmsn: { value: false },
        auc: { value: false },
        fore: { value: false },
        mf: { value: false },
        land: { value: false },
        manu: { value: false },
        app: { value: true },
      },
    };

    return `/${formattedCity}-${formattedState}/rentals/?searchQueryState=${encodeURIComponent(JSON.stringify(queryParams))}`;
  }
  return EXTERNAL_URLS.RENTALS_SEARCH_PAGE_URL;
}

export function ApplicationSubmitted({ onClose, userStatuses, submittedApplication }: Props) {
  const applicationContext = useApplicationContextOrUndefined();
  const daysLeftOnPayment = getDaysLeftOnPayment(userStatuses);
  const purchaseExpectationsTreatment = usePurchaseExpectationsTreatment();
  const isSubmitted = isSubmittedApplication(submittedApplication);
  const requestedZillowPayments = useRenterRequestedZillowPayments(
    isSubmitted ? submittedApplication.applicationId : '',
    {
      enabled: isSubmitted,
    },
  );

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      body={
        <>
          <Heading level={1} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            Congrats! You {submittedApplication.type === SUBMISSION_TYPES.SUBMITTED ? `submitted` : `shared`} your
            application
          </Heading>
          <Heading level={2} textStyle="body-lg-bold">
            Next steps
          </Heading>
          <Paragraph>
            <List.Root appearance="bullet" css={{ marginBottom: 'tighter' }}>
              <List.Item>
                The rental manager will take it from here. You can reach out to them about next steps.
              </List.Item>
              {daysLeftOnPayment > 0 && (
                <List.Item>
                  You can reuse this application at{' '}
                  {purchaseExpectationsTreatment === 'on' ? (
                    <Anchor
                      href={getSearchResultsPageUrl(
                        submittedApplication.address.city,
                        submittedApplication.address.state,
                      )}
                      onClick={() => emitPostSubmitParticipatingPropertiesLinkEvent(applicationContext)}
                    >
                      participating properties
                    </Anchor>
                  ) : (
                    'participating properties'
                  )}{' '}
                  for the next {pluralize('day', daysLeftOnPayment)}.
                </List.Item>
              )}
              {requestedZillowPayments && (
                <List.Item>
                  We’ll send a request for Zillow payments to your landlord if your application is accepted.
                </List.Item>
              )}
            </List.Root>
          </Paragraph>
          <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
            Zillow is committed to fair, inclusive housing. Know and exercise your{' '}
            <Anchor href={FAIR_HOUSING_URL} target="_blank" rel="noopener noreferrer">
              {' '}
              fair housing rights
            </Anchor>
            .
          </Paragraph>
        </>
      }
      footer={
        purchaseExpectationsTreatment === 'on' ? (
          <Button
            tone="brand"
            emphasis="filled"
            asChild
            onClick={() => emitPostSubmitApplyToMorePropertiesCtaEvent(applicationContext)}
          >
            <a href={getSearchResultsPageUrl(submittedApplication.address.city, submittedApplication.address.state)}>
              Apply to more properties
            </a>
          </Button>
        ) : (
          <Button tone="brand" emphasis="filled" onClick={onClose}>
            Go to your dashboard
          </Button>
        )
      }
    />
  );
}
